import Axios from 'axios'
import _, { get, remove } from 'lodash'
import { vueAppApiBaseAddress } from '@/utils/mixins'
import * as Constants from '../../constants'

let initialState = {
  // TODO remove after all components migrated to new polling mechanism.
  apparatusSubscriptionsGettingReady: [],
  subscriptionCancelInitiated: false,
  /**
   * Array of {apparatusIds: number[], pendingProductId: string, isPendingCancel: boolean } objects
   * that represent pending subscription changes of some kind (upgrade, update, downgrade, cadence change, etc.)
   * that we have not received a platform acknowledgement for.
   * This array should only ever have one entry because the most recent pending change/cancellation should prevail.
   */
  pendingSubscriptionChanges: []
}

export const state = _.cloneDeep(initialState)
export const getters = {
  apparatusSubscriptionsGettingReady: (state, getters, rootState) => state.apparatusSubscriptionsGettingReady,
  subscriptionCancelInitiated: (state, getters, rootState) => state.subscriptionCancelInitiated,
  /** Retrieve current list of objects for which we are awaiting platform acknowledgement of a subscription change. The objects are of type:
   * {
   *    // Apparatus ids involved in the new subscription.
   *    apparatusIds: number[],
   *    // The product id of the pending product. Could be null/empty if the change is a pending cancel.
   *    pendingProductId: string,
   *    // Whether or not the subscription change is a cancel.
   *    isPendingCancel: boolean
   * }
   *
   **/
  pendingSubscriptionChanges: (state, getters, rootState) => state.pendingSubscriptionChanges
}
export const mutations = {
  setPremiumFeaturesGettingReadyInfo(state, apparatusSubscriptionData) {
    state.apparatusSubscriptionsGettingReady = [...state.apparatusSubscriptionsGettingReady, apparatusSubscriptionData]
  },
  /** Replaces pendingSubscriptionChanges with a new pending subscription change to the store state. pendingChangeInfo is of type {apparatusIds: number[], pendingProductId: string, isPendingCancel: boolean }. */
  setPendingSubscriptionChanges(state, pendingChangeInfo) {
    state.pendingSubscriptionChanges = [pendingChangeInfo]
  },
  /** Removes pending subscription data. */
  removePendingSubscriptionChange(state) {
    state.pendingSubscriptionChanges = []
  },
  /** Unused. */
  clearPremiumFeaturesGettingReadyInfo(state) {
    state.apparatusSubscriptionsGettingReady = []
  },
  /** Unused. */
  removePremiumFeaturesGettingReadyInfo(state, apparatusId) {
    state.apparatusSubscriptionsGettingReady = state.addPremiumFeaturesGettingReadyInfo?.filter(
      apparatus => apparatus.apparatusId !== apparatusId
    )
  },
  // TODO look into this.
  setSubscriptionsCancelInitiated(state, isSubscriptionCancelInitiated) {
    state.subscriptionCancelInitiated = isSubscriptionCancelInitiated
  }
}

export const actions = {
  /** Update pending subscription changes. pendingChangeInfo is type {apparatusIds: number[], pendingProductId: string, isPendingCancel: boolean }. */
  async addPendingSubscriptionChanges({ commit, state }, pendingChangeInfo) {
    commit('setPendingSubscriptionChanges', pendingChangeInfo)
  },
  /** Removes all pending subscription data from the store state. */
  deletePendingSubscriptionChanges({ commit, state }) {
    commit('removePendingSubscriptionChange')
  },
  async addPremiumFeaturesGettingReadyInfo({ commit }, apparatusSubscriptionData) {
    commit('setPremiumFeaturesGettingReadyInfo', apparatusSubscriptionData)
  },
  /** Unused. */
  async deletePremiumFeaturesGettingReadyInfo({ commit }, apparatusId) {
    commit('removePremiumFeaturesGettingReadyInfo', apparatusId)
  },
  // Unused. TODO look into this.
  async setSubscriptionPendingCancel({ commit }, isSubscriptionCancelInitiated) {
    commit('setSubscriptionsCancelInitiated', isSubscriptionCancelInitiated)
  },
  /** Unused. */
  async getSubscriptionProductByApparatusId({ commit }, apparatusId) {
    return await Axios.get(vueAppApiBaseAddress() + `v1/Subscription/products/${apparatusId}`, {
      withCredentials: true
    })
  },
  /** Unused. */
  async getSubscriptionProductsByDeviceType({ commit }, deviceType) {
    return await Axios.get(vueAppApiBaseAddress() + `v1/Subscription/products/Generator/${deviceType}`, {
      withCredentials: true
    })
  },
  async getActivePaymentMethod({ commit }, organizationId) {
    return await Axios.get(vueAppApiBaseAddress() + `v1/Subscription/payment/${organizationId}`, {
      withCredentials: true
    })
  },
  async changeBillingAccountProduct({ commit }, request) {
    return await Axios.put(vueAppApiBaseAddress() + `v1/Subscription/products/change`, request, {
      withCredentials: true
    })
  },
  async getSubscriptionDetails({ commit }, organizationId) {
    return await Axios.get(vueAppApiBaseAddress() + `v6/Subscription/details/${organizationId}`, {
      withCredentials: true
    })
  },
  async removeBillingAccountPaymentMethod({ commit }, request) {
    return await Axios.put(
      vueAppApiBaseAddress() + `v1/Subscription/payment/${parseInt(request.organizationId)}/${request.paymentId}`,
      {},
      {
        withCredentials: true
      }
    )
  },
  async addBillingAccountPaymentMethod({ commit }, request) {
    return await Axios.post(
      vueAppApiBaseAddress() + `v1/Subscription/payment/${parseInt(request.organizationId)}`,
      {
        cardNumber: request.cardNumber,
        cvv: request.cvv,
        expirationMonth: request.expirationMonth,
        expirationYear: request.expirationYear
      },
      {
        withCredentials: true
      }
    )
  },
  async getApparatusSubscription({ commit }, request) {
    return await Axios.get(
      vueAppApiBaseAddress() + `v1/Subscription/account/${request.organizationId}/${request.apparatusId}`,
      {
        withCredentials: true
      }
    )
  },
  /** Unused. */
  async getAllApparatusSubscriptions({ commit }, request) {
    return await Axios.get(vueAppApiBaseAddress() + `v1/Subscription/account/active/${request.organizationId}`, {
      withCredentials: true
    })
  },
  async cancelApparatusSubscription({ commit }, request) {
    return await Axios.delete(
      vueAppApiBaseAddress() + `v1/Subscription/products/${request.organizationId}/${request.apparatusId}`,
      {
        withCredentials: true
      }
    )
  },
  async findAccountInvoices({ commit }, request) {
    return await Axios.get(vueAppApiBaseAddress() + `v1/Subscription/find/invoices/${request.organizationId}`, {
      withCredentials: true
    })
  },
  async downloadInvoice({ commit }, request) {
    return await Axios.get(
      vueAppApiBaseAddress() + `v1/Subscription/download/invoice/${request.organizationId}/${request.invoiceId}`,
      {
        withCredentials: true,
        responseType: 'blob'
      }
    )
  },
  async getSubscriptionProductsByStoreOrganization({ commit }, request) {
    return await Axios.get(
      vueAppApiBaseAddress() +
        `v5/Subscription/recommendedStoreProducts/${Constants.WebSubscriptionStore}/${request.organizationId}`,
      {
        withCredentials: true
      }
    )
  },
  async getSubscriptionProductsByStoreOrganizationApparatusToAdd({ commit }, request) {
    return await Axios.get(
      vueAppApiBaseAddress() +
        `v5/Subscription/recommendedStoreProducts/${Constants.WebSubscriptionStore}/${request.organizationId}?apparatusTypeToAdd=${request.apparatusTypeToAdd}`,
      {
        withCredentials: true
      }
    )
  },
  /** Unused. */
  async getSubscriptionProductsByStoreOrganizationApparatusToRemove({ commit }, request) {
    return await Axios.get(
      vueAppApiBaseAddress() +
        `v5/Subscription/recommendedStoreProducts/${request.store}/${request.organizationId}?apparatusTypeToRemove=${request.apparatusTypeToRemove}`,
      {
        withCredentials: true
      }
    )
  },
  async getAllWebSubscriptionProducts({ commit }) {
    return await Axios.get(vueAppApiBaseAddress() + `v5/Subscription/products/all/${Constants.WebSubscriptionStore}`, {
      withCredentials: true
    })
  },
  async updateSubscriptionProduct({ commit }, request) {
    return await Axios.put(
      vueAppApiBaseAddress() +
        `v1/Subscription/updateSubscriptionProduct/${request.organizationId}/${Constants.SubscriptionStoreTypes.WEB}/${request.productId}`,
      {},
      {
        withCredentials: true
      }
    )
  },
  async createPurchaseIntent({ commit }, request) {
    return await Axios.post(
      vueAppApiBaseAddress() +
        `v4/Subscription/purchaseIntent/${request.organizationId}/${Constants.SubscriptionStoreTypes.WEB}/${request.productId}`,
      request.apparatusIds,
      {
        withCredentials: true
      }
    )
  },
  async updateSubscriptionApparatuses({ commit }, request) {
    return await Axios.put(
      vueAppApiBaseAddress() +
        `v1/Subscription/updateSubscriptionApparatuses/${request.organizationId}/${request.store}/${request.productId}`,
      request.apparatusIds,
      {
        withCredentials: true
      }
    )
  },
  async cancelSubscriptionProduct({ commit }, request) {
    return await Axios.delete(
      vueAppApiBaseAddress() +
        `v1/Subscription/subscriptionProduct/${request.organizationId}/${request.store}/${request.productId}`,
      {
        withCredentials: true,
        responseType: 'blob'
      }
    )
  },
  async cancelSubscriptionProductImmediately({ commit }, request) {
    return await Axios.put(vueAppApiBaseAddress() + `v1/Subscription/subscriptionProduct/deleteImmediately`, request, {
      withCredentials: true
    })
  },
  async storeSubscriptionTermsConsent({ commit }, organizationId) {
    return await Axios.post(
      vueAppApiBaseAddress() + `v4/Subscription/storeSubscriptionTermsConsent/${organizationId}`,
      {},
      {
        withCredentials: true,
        responseType: 'blob'
      }
    )
  }
}
export default {
  state,
  getters,
  actions,
  mutations
}
